/**
 * The links tool results carry, keyed by tool-call id — what the inline `sphere-handle:`
 * link renderer (toolLinks.ts) resolves the handles the model writes into its prose against.
 *
 * sphere-mcp's `toolResult` emits a `structuredContent` envelope: `{ original: <the tool's
 * own payload>, ...keys the server added for the client }`. Only the server-added half is
 * kept, and `links` is read out of it: that half is what was withheld from the model (a
 * presigned URL reaches the client and must not reach the context).
 *
 * Two writers fill it:
 * - **the transcript** — serverToolResults.ts records every settled tool part's
 *   `structuredContent`, live or restored alike;
 * - **the restore of a conversation stored before MDOT-25078**, whose tool output is text
 *   and whose envelopes live only in the conversation's `toolResults` map
 *   ({@link hydrateToolResults}).
 *
 * Module-level rather than React state because the writers and the renderer outlive any
 * one component instance.
 *
 * CROSS-REPO CONTRACT, kept in sync by hand: the envelope is sphere-mcp's
 * `src/server/mcp/utils.ts`; the `toolResults` map is its history-store.ts `tool_results`
 * column, returned by GET `/v1/conversations/:id`.
 */

/** One tool call's server-added keys. */
type StoredResult = Record<string, unknown>;

/**
 * One external link a tool produced, mirroring sphere-mcp's `ToolLink`.
 *
 * CROSS-REPO CONTRACT: sphere-mcp's `src/server/mcp/utils.ts` holds the definition; this
 * is the reader's copy, kept in sync by hand. Narrowed on arrival ({@link toolLinksOf})
 * rather than trusted, because it comes back out of a store that never interpreted it.
 */
export type ToolLink = {
	/** The handle the model links to (`sphere-handle://<ref>`), and this index's key. */
	ref: string;
	/**
	 * The address. The value the split exists to keep out of the model's context.
	 *
	 * Absent on a `"page"` link: that kind carries no server-side href at all, because the
	 * URL depends on the viewer's own entitlements and must be resolved client-side, live, at
	 * render time — see `entityType` / `entityId` below.
	 */
	href?: string;
	/**
	 * What the link is for. Advisory for every other value; a `"page"` link is the one whose
	 * `href` is resolved from `entityType` / `entityId` instead of stored verbatim — see
	 * toolLinks.ts's `resolveToolLink`.
	 */
	rel: string;
	/**
	 * What a NON-INLINE consumer would show. The inline renderer does not use it: the link's
	 * words are whatever the model wrote around the handle, which it is better placed to
	 * choose than the tool is ("downloaded [here]", not a filename said twice).
	 */
	label: string;
	/** ISO-8601 instant after which `href` stops working, where the producer knows one. */
	expiresAt?: string;
	/** A `"page"` link's kind of Sphere entity — paired with `entityId`, present only together. */
	entityType?: string;
	/** A `"page"` link's entity id — paired with `entityType`, present only together. */
	entityId?: string;
	/** A `"page"` link's as-of day (`YYYY-MM-DD`): the entity's page pinned to that date. */
	asOf?: string;
};

/** Insertion-ordered, which is what makes a later entry win a ref collision. */
const resultsByToolCallId = new Map<string, StoredResult>();

/** The server-added keys of an envelope, or `undefined` when it carries none. */
function addedKeys(structuredContent: unknown): StoredResult | undefined {
	if (typeof structuredContent !== "object" || structuredContent === null || Array.isArray(structuredContent)) {
		return undefined;
	}
	const { original: _original, ...added } = structuredContent as Record<string, unknown>;
	return Object.keys(added).length > 0 ? added : undefined;
}

/**
 * Record what a tool call returned. A no-op for a result with no server-added keys, which
 * is every client-side tool and any error. Re-recording an id replaces it.
 */
export function setToolResult(toolCallId: string, structuredContent: unknown): void {
	const entry = addedKeys(structuredContent);
	if (!entry) return;
	resultsByToolCallId.delete(toolCallId);
	resultsByToolCallId.set(toolCallId, entry);
}

/** Narrow one entry's `links` into usable {@link ToolLink}s, dropping anything malformed. */
function toolLinksOf(entry: StoredResult): ToolLink[] {
	const raw = entry.links;
	if (!Array.isArray(raw)) return [];
	const links: ToolLink[] = [];
	for (const candidate of raw) {
		if (typeof candidate !== "object" || candidate === null) continue;
		const { ref, href, rel, label, expiresAt, entityType, entityId, asOf } = candidate as Record<string, unknown>;
		if (typeof ref !== "string" || ref === "") continue;
		const hasHref = typeof href === "string" && href !== "";
		const hasEntity =
			typeof entityType === "string" && entityType !== "" && typeof entityId === "string" && entityId !== "";
		// `ref` is what a link IS: without it there is nothing to resolve. Beyond that, a link is
		// USABLE either as a plain address (`href`) or as an entity to resolve client-side
		// (`entityType` + `entityId`) — anything with neither is dropped before it reaches the
		// resolver. `rel` and `label` are presentation and are defaulted, so a producer that adds
		// a field this copy has not learned about still renders.
		if (!hasHref && !hasEntity) continue;
		links.push({
			ref,
			...(hasHref ? { href } : {}),
			rel: typeof rel === "string" && rel !== "" ? rel : "external",
			label: typeof label === "string" && label !== "" ? label : ref,
			...(typeof expiresAt === "string" ? { expiresAt } : {}),
			...(hasEntity ? { entityType, entityId } : {}),
			...(hasEntity && typeof asOf === "string" && asOf !== "" ? { asOf } : {}),
		});
	}
	return links;
}

/**
 * Every link currently held, indexed by the handle the model writes — which is what the
 * inline renderer resolves against.
 *
 * Refs are globally unique (sphere-mcp mints them from a uuid), so a flat index across
 * tool calls is correct, and is what lets a handle resolve from anywhere in the prose —
 * including a turn later than the one that produced it. A later entry wins on the
 * vanishingly unlikely collision, which is the fresher link.
 */
export function linksByRef(): Map<string, ToolLink> {
	const index = new Map<string, ToolLink>();
	for (const entry of resultsByToolCallId.values()) {
		for (const link of toolLinksOf(entry)) index.set(link.ref, link);
	}
	return index;
}

/**
 * Merge a restored conversation's stored `toolResults` map into the live one — call it on
 * the restore, before the transcript renders.
 *
 * Additive only, and the live entry wins: a result read off the transcript is the one the
 * server sent with the tool call. The map arrives verbatim from a store that never
 * interpreted it, so every entry is narrowed before it is kept.
 */
export function hydrateToolResults(stored: unknown): void {
	if (typeof stored !== "object" || stored === null || Array.isArray(stored)) return;
	for (const [toolCallId, envelope] of Object.entries(stored)) {
		if (resultsByToolCallId.has(toolCallId)) continue;
		const entry = addedKeys(envelope);
		if (entry) resultsByToolCallId.set(toolCallId, entry);
	}
}

/** Drop everything. Tests only; a real session ends by being unloaded. */
export function clearToolResults(): void {
	resultsByToolCallId.clear();
}
